// =============================================================================
// FILE: packages/renderer/src/lib/contextPackageGenerator.ts
// PURPOSE: Renders the "Multi-Trade Design Package" PDF from a MultiTradeResult.
//   Client-side (jsPDF), branded dark theme, SVG->canvas rasterization so we
//   can also surface a page-1 preview for the ingestor fly-out.
//     Page 1 — Cover (project, trades, total fee, timeline)
//     Page 2 — Trade scope & fee breakdown
//     Page 3 — Deliverables / diagram set
// =============================================================================

import { jsPDF } from 'jspdf';
import type { MultiTradeResult } from './contextAnalyzer.js';

const PAGE_W = 1754;
const PAGE_H = 1240;

export interface PackageResult { blobUrl: string; fileName: string; firstPageDataUrl: string }

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function frame(inner: string, footer: string): string {
  return `
  <svg xmlns="http://www.w3.org/2000/svg" width="${PAGE_W}" height="${PAGE_H}" viewBox="0 0 ${PAGE_W} ${PAGE_H}">
    <defs>
      <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stop-color="#070b14"/><stop offset="100%" stop-color="#0b1220"/>
      </linearGradient>
      <pattern id="grid" width="48" height="48" patternUnits="userSpaceOnUse">
        <path d="M 48 0 L 0 0 0 48" fill="none" stroke="rgba(40,68,116,0.14)" stroke-width="1"/>
      </pattern>
    </defs>
    <rect width="${PAGE_W}" height="${PAGE_H}" fill="url(#bg)"/>
    <rect width="${PAGE_W}" height="${PAGE_H}" fill="url(#grid)"/>
    <rect x="40" y="40" width="${PAGE_W - 80}" height="${PAGE_H - 80}" rx="18" fill="none" stroke="rgba(37,181,168,0.28)" stroke-width="2"/>
    ${inner}
    <g transform="translate(${PAGE_W - 130},${PAGE_H - 130})" opacity="0.96">
      <circle cx="0" cy="0" r="86" fill="none" stroke="#25B5A8" stroke-width="2.5" opacity="0.55"/>
      <text x="0" y="-6" text-anchor="middle" font-family="Inter,sans-serif" font-weight="900" font-size="46" fill="#E0FFFE">R²</text>
      <text x="0" y="34" text-anchor="middle" font-family="Inter,sans-serif" font-weight="800" font-size="15" letter-spacing="2" fill="#5EEAD4">CERTIFIED</text>
      <text x="0" y="54" text-anchor="middle" font-family="Inter,sans-serif" font-weight="700" font-size="11" letter-spacing="3" fill="rgba(148,172,210,0.85)">BY RCD²</text>
    </g>
    <text x="88" y="${PAGE_H - 70}" font-family="Inter,sans-serif" font-size="20" fill="rgba(148,172,210,0.7)" letter-spacing="1.5">${footer}</text>
  </svg>`;
}

function coverSvg(r: MultiTradeResult, project: string, author: string, dateStr: string): string {
  const active = r.packages.filter((p) => p.detected);
  const chips = active.map((p, i) => `
    <g transform="translate(140,${700 + i * 66})">
      <rect x="0" y="-34" width="900" height="52" rx="10" fill="${p.color}18" stroke="${p.color}" stroke-width="1.5"/>
      <text x="20" y="2" font-family="Inter,sans-serif" font-weight="700" font-size="26" fill="${p.color}">${esc(p.label)}</text>
      <text x="880" y="2" text-anchor="end" font-family="Inter,sans-serif" font-weight="700" font-size="24" fill="#E2E8F0">$${p.estimateUsd.toLocaleString()}</text>
    </g>`).join('');
  const inner = `
    <text x="140" y="150" font-family="Inter,sans-serif" font-size="26" fill="rgba(37,181,168,0.85)" letter-spacing="6">RCD² MASTER ARCHITECT</text>
    <text x="140" y="250" font-family="Inter,sans-serif" font-weight="800" font-size="90" fill="#F0F5FF" letter-spacing="-1">Multi-Trade Design Package</text>
    <rect x="140" y="290" width="${PAGE_W - 280}" height="2" fill="rgba(37,181,168,0.4)"/>
    <text x="140" y="380" font-family="Inter,sans-serif" font-size="24" fill="rgba(148,172,210,0.7)" letter-spacing="3">PROJECT</text>
    <text x="140" y="426" font-family="Inter,sans-serif" font-weight="700" font-size="42" fill="#E2E8F0">${esc(project)}</text>
    <text x="140" y="510" font-family="Inter,sans-serif" font-size="24" fill="rgba(148,172,210,0.7)" letter-spacing="3">ENGINEER OF RECORD</text>
    <text x="140" y="556" font-family="Inter,sans-serif" font-weight="700" font-size="38" fill="#E2E8F0">${esc(author)}</text>
    <text x="140" y="640" font-family="Inter,sans-serif" font-size="24" fill="rgba(148,172,210,0.7)" letter-spacing="3">SCOPE OF WORK</text>
    ${chips}
    <g transform="translate(${PAGE_W - 560},360)">
      <rect x="0" y="0" width="420" height="150" rx="14" fill="rgba(37,181,168,0.1)" stroke="#25B5A8" stroke-width="2"/>
      <text x="30" y="46" font-family="Inter,sans-serif" font-size="22" fill="rgba(148,172,210,0.8)" letter-spacing="2">PRELIMINARY DESIGN FEE</text>
      <text x="30" y="112" font-family="Inter,sans-serif" font-weight="800" font-size="60" fill="#5EEAD4">$${r.totalUsd.toLocaleString()}</text>
    </g>
    <g transform="translate(${PAGE_W - 560},540)">
      <rect x="0" y="0" width="420" height="120" rx="14" fill="rgba(245,158,11,0.1)" stroke="#F59E0B" stroke-width="2"/>
      <text x="30" y="46" font-family="Inter,sans-serif" font-size="22" fill="rgba(148,172,210,0.8)" letter-spacing="2">EST. DELIVERY</text>
      <text x="30" y="98" font-family="Inter,sans-serif" font-weight="800" font-size="46" fill="#FBBF24">~${r.timelineWeeks} weeks</text>
    </g>
    <text x="140" y="${PAGE_H - 120}" font-family="Inter,sans-serif" font-size="22" fill="rgba(148,172,210,0.65)">Issued ${esc(dateStr)} · Auto-generated by the RCD² Master Context Ingestor</text>
  `;
  return frame(inner, 'RCD² INTELLIGENCE ENGINE · MULTI-TRADE DESIGN PACKAGE · PAGE 1 / 3');
}

function scopeSvg(r: MultiTradeResult): string {
  const active = r.packages.filter((p) => p.detected);
  let y = 340;
  const blocks = active.map((p) => {
    const header = `
      <text x="140" y="${y}" font-family="Inter,sans-serif" font-weight="800" font-size="34" fill="${p.color}">${esc(p.label)}</text>
      <text x="${PAGE_W - 140}" y="${y}" text-anchor="end" font-family="Inter,sans-serif" font-weight="700" font-size="30" fill="#E2E8F0">$${p.estimateUsd.toLocaleString()}</text>
      <text x="140" y="${y + 34}" font-family="Inter,sans-serif" font-size="22" fill="rgba(148,172,210,0.7)" letter-spacing="2">${esc(p.division)}</text>`;
    y += 74;
    const lines = p.items.map((it) => {
      const line = `<circle cx="152" cy="${y - 8}" r="4" fill="${p.color}"/><text x="176" y="${y}" font-family="Inter,sans-serif" font-size="24" fill="rgba(226,232,240,0.9)">${esc(it)}</text>`;
      y += 40;
      return line;
    }).join('');
    y += 26;
    return header + lines;
  }).join('');
  const inner = `
    <text x="140" y="150" font-family="Inter,sans-serif" font-size="26" fill="rgba(37,181,168,0.8)" letter-spacing="6">SCOPE & FEE BREAKDOWN</text>
    <text x="140" y="220" font-family="Inter,sans-serif" font-weight="800" font-size="60" fill="#F0F5FF">Trade Coordination Matrix</text>
    ${blocks}`;
  return frame(inner, 'SCOPE & FEE BREAKDOWN · PAGE 2 / 3');
}

function deliverablesSvg(r: MultiTradeResult): string {
  const items = r.diagrams.map((d, i) => `
    <g transform="translate(${140 + (i % 2) * 780},${360 + Math.floor(i / 2) * 90})">
      <rect x="0" y="-40" width="740" height="66" rx="12" fill="rgba(14,20,38,0.9)" stroke="rgba(37,181,168,0.4)" stroke-width="1.5"/>
      <text x="24" y="0" font-family="Inter,sans-serif" font-weight="700" font-size="26" fill="#E2E8F0">${esc(d)}</text>
      <text x="716" y="0" text-anchor="end" font-family="Inter,sans-serif" font-size="20" fill="#5EEAD4" letter-spacing="2">INCLUDED</text>
    </g>`).join('');
  const inner = `
    <text x="140" y="150" font-family="Inter,sans-serif" font-size="26" fill="rgba(37,181,168,0.8)" letter-spacing="6">DELIVERABLES</text>
    <text x="140" y="220" font-family="Inter,sans-serif" font-weight="800" font-size="60" fill="#F0F5FF">Drawing & Specification Set</text>
    <text x="140" y="270" font-family="Inter,sans-serif" font-size="24" fill="rgba(148,172,210,0.7)">Coordinated across all detected trades · sealed under the RCD² certification stamp</text>
    ${items}`;
  return frame(inner, 'DELIVERABLES · PAGE 3 / 3');
}

function rasterize(svg: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = PAGE_W; canvas.height = PAGE_H;
      const ctx = canvas.getContext('2d')!;
      ctx.fillStyle = '#070b14'; ctx.fillRect(0, 0, PAGE_W, PAGE_H);
      ctx.drawImage(img, 0, 0, PAGE_W, PAGE_H);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

export async function generateDesignPackage(
  r: MultiTradeResult,
  project: string,
  author: string,
  onProgress?: (dataUrl: string, i: number) => void,
): Promise<PackageResult> {
  const dateStr = new Date().toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' });
  const svgs = [coverSvg(r, project, author, dateStr), scopeSvg(r), deliverablesSvg(r)];
  const pages: string[] = [];
  for (let i = 0; i < svgs.length; i++) {
    const dataUrl = await rasterize(svgs[i]!);
    pages.push(dataUrl);
    onProgress?.(dataUrl, i);
  }
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const pw = pdf.internal.pageSize.getWidth();
  const ph = pdf.internal.pageSize.getHeight();
  pages.forEach((url, i) => { if (i > 0) pdf.addPage(); pdf.addImage(url, 'PNG', 0, 0, pw, ph, undefined, 'FAST'); });
  const fileName = `RCD2-MultiTrade-${project.replace(/[^a-z0-9]+/gi, '-')}-${Date.now()}.pdf`;
  const blobUrl = URL.createObjectURL(pdf.output('blob'));
  return { blobUrl, fileName, firstPageDataUrl: pages[0]! };
}
